import React, { useEffect, useState } from "react";
import Page from "../../components/Page";
import { 
  IconBluetooth, 
  IconRefresh, 
  IconSearch, 
  IconAlertCircle, 
  IconCheck, 
  IconServer, 
  IconInfoCircle, 
  IconDeviceHeartMonitor,
  IconWifi,
  IconPlus,
  IconTrash,
  IconPrinter,
  IconSend,
  IconNetwork,
  IconDeviceDesktop,
  IconStar,
  IconChecklist
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import toast from "react-hot-toast";
import { 
  fetchPrintersFromWebSocketAgent, 
  setBluetoothDevice, 
  getBluetoothDevice, 
  printViaWebBluetooth,
  getNetworkPrinters,
  saveNetworkPrinter,
  deleteNetworkPrinter,
  testPrinter,
  printKOT
} from "../../helpers/PrinterHelper";
import { useCategories, updateCategory } from "../../controllers/settings.controller";
import { mutate } from "swr";

const SafeIconPrinter = ({ size = 24, stroke = 2, className = "", ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
    <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
    <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z" />
  </svg>
);

const isElectron = typeof window !== 'undefined' && Boolean(
  window.isElectron ||
  window.electron ||
  (window.process && window.process.type) ||
  (window.require && typeof window.require === 'function')
);

export default function PrintersPage() {
  const [activeTab, setActiveTab] = useState("printers"); // "printers" | "categories"
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testingPrinter, setTestingPrinter] = useState(null);

  // Network / WiFi Printers
  const [networkPrinters, setNetworkPrinters] = useState(getNetworkPrinters());
  const [scanningNetwork, setScanningNetwork] = useState(false);
  const [showAddIpModal, setShowAddIpModal] = useState(false);
  const [ipForm, setIpForm] = useState({ name: "", ip: "", port: "9100", format: "80" });
  const [verifyingIp, setVerifyingIp] = useState(false);

  // Default Bill Printer State
  const [defaultBillPrinter, setDefaultBillPrinter] = useState(localStorage.getItem("billPrinter") || "");
  const [pageFormat, setPageFormat] = useState(localStorage.getItem("page_format") || "80");
  const [billCopies, setBillCopies] = useState(parseInt(localStorage.getItem("printCount")) || 1);

  // Category KOT Copies State
  const [categoryCopies, setCategoryCopies] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("category_kot_copies") || "{}");
    } catch (e) {
      return {};
    }
  });

  const handleSetBillCopies = (val) => {
    const c = Math.max(1, parseInt(val) || 1);
    setBillCopies(c);
    localStorage.setItem("printCount", c);
    toast.success(`Bill copies set to ${c}`);
  };

  const handleSetCategoryCopies = (categoryId, val) => {
    const c = Math.max(1, parseInt(val) || 1);
    const updated = { ...categoryCopies, [categoryId]: c };
    setCategoryCopies(updated);
    localStorage.setItem("category_kot_copies", JSON.stringify(updated));
    const cat = categories.find(c => c.id === categoryId);
    toast.success(`${cat?.title || 'Category'} KOT copies set to ${c}`);
  };

  // Category KOT Routing Data
  const { APIURL: categoriesApiUrl, data: categories = [], isLoading: categoriesLoading } = useCategories();
  const [savingCategory, setSavingCategory] = useState(null);

  // Bluetooth Printer States
  const [btConnected, setBtConnected] = useState(!!getBluetoothDevice());
  const [btDeviceName, setBtDeviceName] = useState(getBluetoothDevice()?.name || "");

  // Web Agent States
  const [agentUrl, setAgentUrl] = useState(localStorage.getItem("printer_agent_url") || "ws://127.0.0.1:8124");
  const [agentStatus, setAgentStatus] = useState("disconnected");

  // Fetch local OS printers
  const fetchPrinters = async () => {
    setLoading(true);
    if (isElectron) {
      try {
        let list = [];
        if (window.electron && window.electron.getPrinters) {
          list = await window.electron.getPrinters();
        } else if (window.require) {
          const { ipcRenderer } = window.require('electron');
          list = await ipcRenderer.invoke('get-printers');
        }
        setPrinters(list || []);
        toast.success("Printers list refreshed");
      } catch (error) {
        console.error(error);
        toast.error("Failed to fetch OS printers");
      } finally {
        setLoading(false);
      }
    } else {
      // Browser mode: fetch via WebSocket Agent
      setAgentStatus("checking");
      try {
        const isConnected = await checkAgentConnection(agentUrl);
        if (isConnected) {
          setAgentStatus("connected");
          const list = await fetchPrintersFromWebSocketAgent();
          setPrinters(list || []);
          toast.success("Printers discovered via Web Agent!");
        } else {
          setAgentStatus("disconnected");
          setPrinters([]);
          toast.error("Could not connect to WebSocket Print Agent.");
        }
      } catch (error) {
        console.error(error);
        setAgentStatus("disconnected");
        setPrinters([]);
        toast.error("Failed to fetch printers: " + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // WiFi / Network Printer Scanner
  const scanNetworkPrinters = async () => {
    if (!isElectron) {
      toast.error("WiFi / Subnet scan requires the Electron Desktop application.");
      return;
    }

    setScanningNetwork(true);
    toast.loading("Scanning local network for thermal printers on port 9100...");
    try {
      let res;
      if (window.electron && window.electron.scanNetworkPrinters) {
        res = await window.electron.scanNetworkPrinters({ port: 9100 });
      } else if (window.require) {
        const { ipcRenderer } = window.require('electron');
        res = await ipcRenderer.invoke('scan-network-printers', { port: 9100 });
      }

      toast.dismiss();
      if (res && res.success && Array.isArray(res.printers)) {
        if (res.printers.length === 0) {
          toast("Scan complete: No new network thermal printers detected on port 9100. You can also add an IP printer manually.", { icon: "ℹ️" });
        } else {
          toast.success(`Discovered ${res.printers.length} network thermal printer(s)!`);
          let currentList = getNetworkPrinters();
          res.printers.forEach((np) => {
            const exists = currentList.find(p => p.ip === np.ip && p.port === np.port);
            if (!exists) {
              const newEntry = {
                id: `net_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
                name: np.name || `Network Printer (${np.ip})`,
                ip: np.ip,
                port: np.port || 9100,
                format: "80"
              };
              currentList = saveNetworkPrinter(newEntry);
            }
          });
          setNetworkPrinters(currentList);
        }
      } else {
        toast.error(res?.error || "Network scan failed");
      }
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error(`Scan failed: ${err.message}`);
    } finally {
      setScanningNetwork(false);
    }
  };

  // Verify and Save Manual IP Printer
  const handleVerifyAndSaveIpPrinter = async () => {
    const { name, ip, port, format } = ipForm;
    if (!ip || !ip.trim()) {
      toast.error("Please enter a valid IP address");
      return;
    }
    const cleanIp = ip.trim();
    const cleanPort = parseInt(port) || 9100;
    const cleanName = name.trim() || `Network Printer (${cleanIp})`;

    setVerifyingIp(true);
    toast.loading(`Testing connection to ${cleanIp}:${cleanPort}...`);
    try {
      if (isElectron) {
        let res;
        if (window.electron && window.electron.testNetworkPrinter) {
          res = await window.electron.testNetworkPrinter({ ip: cleanIp, port: cleanPort });
        } else if (window.require) {
          const { ipcRenderer } = window.require('electron');
          res = await ipcRenderer.invoke('test-network-printer', { ip: cleanIp, port: cleanPort });
        }

        toast.dismiss();
        if (res && res.reachable) {
          toast.success(`Connected to ${cleanIp}:${cleanPort}! Saving printer...`);
        } else {
          toast("Could not verify TCP handshake on port 9100, but saving printer anyway.", { icon: "⚠️" });
        }
      } else {
        toast.dismiss();
      }

      const newEntry = {
        id: `net_${Date.now()}`,
        name: cleanName,
        ip: cleanIp,
        port: cleanPort,
        format: format || "80"
      };

      const updated = saveNetworkPrinter(newEntry);
      setNetworkPrinters(updated);
      setShowAddIpModal(false);
      setIpForm({ name: "", ip: "", port: "9100", format: "80" });
      toast.success(`Network printer "${cleanName}" saved!`);
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error(`Connection check failed: ${err.message}`);
    } finally {
      setVerifyingIp(false);
    }
  };

  const handleDeleteNetworkPrinter = (id, name) => {
    if (window.confirm(`Are you sure you want to remove network printer "${name}"?`)) {
      const updated = deleteNetworkPrinter(id);
      setNetworkPrinters(updated);
      toast.success("Network printer removed");
    }
  };

  // Universal Test Print
  const handleTestPrint = async (printerConfig) => {
    const targetKey = printerConfig.isIp ? `net_${printerConfig.ip}` : printerConfig.name;
    setTestingPrinter(targetKey);
    toast.loading(`Sending test page to "${printerConfig.name || printerConfig.ip}"...`);

    try {
      const res = await testPrinter({
        name: printerConfig.name,
        isIp: printerConfig.isIp,
        ip: printerConfig.ip,
        port: printerConfig.port || 9100,
        format: printerConfig.format || pageFormat || '80'
      });
      toast.dismiss();
      toast.success(res?.message || "Test page printed successfully!");
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error(`Test print failed: ${err.message}`);
    } finally {
      setTestingPrinter(null);
    }
  };

  // Save Default Bill Printer
  const handleSetDefaultBillPrinter = (printerName) => {
    localStorage.setItem("billPrinter", printerName);
    setDefaultBillPrinter(printerName);
    toast.success(`Default Bill Printer set to "${printerName || 'Default'}"`);
  };

  // Assign Printer to Category
  const handleAssignCategoryPrinter = async (category, newPrinterName) => {
    setSavingCategory(category.id);
    toast.loading(`Assigning printer to ${category.title}...`);
    try {
      // Save immediately to local cache so KOT print uses it instantly
      try {
        localStorage.setItem(`category_printer_${category.id}`, newPrinterName || "");
        let catPrintersMap = {};
        try { catPrintersMap = JSON.parse(localStorage.getItem("category_printers_map") || "{}"); } catch(e) {}
        catPrintersMap[category.id] = newPrinterName || "";
        localStorage.setItem("category_printers_map", JSON.stringify(catPrintersMap));
      } catch (storageErr) {}

      const res = await updateCategory(category.id, category.title, newPrinterName);
      toast.dismiss();
      if (res.status === 200) {
        await mutate(categoriesApiUrl);
        toast.success(`Updated printer for ${category.title}!`);
      } else {
        toast.error("Failed to update category printer");
      }
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to update category printer");
    } finally {
      setSavingCategory(null);
    }
  };

  // Send Sample Test KOT to Category Printer
  const handleTestCategoryKOT = async (category) => {
    const assignedPrinter = category.printer || defaultBillPrinter || "Default";
    toast.loading(`Sending sample KOT to "${assignedPrinter}"...`);
    try {
      const mockItems = [
        {
          id: 991,
          title: `${category.title} Item A`,
          quantity: 2,
          notes: "Spicy / Less oil",
          addons: []
        },
        {
          id: 992,
          title: `${category.title} Special`,
          quantity: 1,
          notes: "Urgent",
          addons: []
        }
      ];

      const printerGroups = {
        [assignedPrinter]: mockItems
      };

      const orderDetails = {
        orderId: `TEST-${Math.floor(100 + Math.random() * 900)}`,
        tokenNo: `${Math.floor(10 + Math.random() * 90)}`,
        table_title: "Table 1 (TEST)",
        waiterName: "Admin Test",
        pageFormat: pageFormat || "80"
      };

      await printKOT(printerGroups, orderDetails);
      toast.dismiss();
      toast.success(`Sample KOT sent to "${assignedPrinter}"!`);
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error(`KOT print failed: ${err.message}`);
    }
  };

  // Quick WebSocket check for browser
  const checkAgentConnection = (urlToTest) => {
    return new Promise((resolve) => {
      try {
        const ws = new WebSocket(urlToTest);
        const timer = setTimeout(() => {
          ws.close();
          resolve(false);
        }, 1500);

        ws.onopen = () => ws.send(JSON.stringify({ type: "ping" }));
        ws.onmessage = (event) => {
          clearTimeout(timer);
          try {
            const res = JSON.parse(event.data);
            resolve(res.type === "pong" && res.status === "connected");
          } catch (e) {
            resolve(false);
          } finally {
            ws.close();
          }
        };
        ws.onerror = () => {
          clearTimeout(timer);
          resolve(false);
        };
      } catch (err) {
        resolve(false);
      }
    });
  };

  // Web Bluetooth handler
  const connectBluetoothPrinter = async () => {
    try {
      if (!navigator.bluetooth) {
        toast.error("Web Bluetooth API is not supported in this browser.");
        return;
      }
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
      });

      toast.loading("Connecting to GATT server...");
      const server = await device.gatt.connect();
      const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
      const characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');

      setBluetoothDevice(device, characteristic);
      setBtConnected(true);
      setBtDeviceName(device.name || "Bluetooth Printer");
      toast.dismiss();
      toast.success(`Connected to ${device.name || "Bluetooth Printer"}`);

      device.addEventListener('gattserverdisconnected', () => {
        setBluetoothDevice(null, null);
        setBtConnected(false);
        setBtDeviceName("");
        toast.error("Bluetooth printer disconnected");
      });
    } catch (error) {
      toast.dismiss();
      console.error(error);
      toast.error(`Failed to connect: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchPrinters();
  }, []);

  return (
    <Page className="px-8 py-6 text-restro-green-dark">
      {/* Page Title & Navigation Tabs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-3xl font-light flex items-center gap-2">
            <SafeIconPrinter size={32} /> Printers & KOT Routing
          </h3>
          <p className="text-sm text-gray-500">
            Discover OS & WiFi network thermal printers, test printing, and route kitchen KOTs by category.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-black/40 p-1.5 rounded-xl border border-restro-border-green">
          <button
            onClick={() => setActiveTab("printers")}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${
              activeTab === "printers"
                ? "bg-restro-green text-white shadow"
                : "text-gray-600 dark:text-gray-300 hover:text-restro-green"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <SafeIconPrinter size={18} /> Printer Devices
            </span>
          </button>
          <button
            onClick={() => setActiveTab("categories")}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${
              activeTab === "categories"
                ? "bg-restro-green text-white shadow"
                : "text-gray-600 dark:text-gray-300 hover:text-restro-green"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <IconChecklist size={18} /> Category KOT Routing
            </span>
          </button>
        </div>
      </div>

      {/* ───────────────────────────────────────────────────────────── */}
      {/* TAB 1: PRINTER DEVICES (OS & NETWORK PRINTERS)               */}
      {/* ───────────────────────────────────────────────────────────── */}
      {activeTab === "printers" && (
        <div className="space-y-8">
          {/* Default Bill Printer Selection Card */}
          <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300 font-bold mb-1">
                <IconStar size={20} className="fill-emerald-500 text-emerald-500" /> Default Bill / Receipt Printer
              </div>
              <p className="text-xs text-emerald-700 dark:text-emerald-400">
                Selected printer will be used for printing estimates, customer bills, and fallback KOTs.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <select
                value={defaultBillPrinter}
                onChange={(e) => handleSetDefaultBillPrinter(e.target.value)}
                className="text-sm rounded-lg px-4 py-2 border border-emerald-300 dark:border-emerald-700 bg-white dark:bg-black font-semibold text-gray-800 dark:text-gray-200 focus:outline-none"
              >
                <option value="">Default (System Default)</option>
                <optgroup label="OS Installed Printers">
                  {printers.map((p, idx) => (
                    <option key={`os-${idx}`} value={p.name}>
                      {p.displayName || p.name}
                    </option>
                  ))}
                </optgroup>
                {networkPrinters.length > 0 && (
                  <optgroup label="WiFi / Network Printers">
                    {networkPrinters.map((np, idx) => (
                      <option key={`net-${idx}`} value={`IP:${np.ip}:${np.port}`}>
                        {np.name} (WiFi: {np.ip}:{np.port})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>

              <select
                value={pageFormat}
                onChange={(e) => {
                  setPageFormat(e.target.value);
                  localStorage.setItem("page_format", e.target.value);
                  toast.success(`Paper format set to ${e.target.value}mm`);
                }}
                className="text-sm rounded-lg px-3 py-2 border border-emerald-300 dark:border-emerald-700 bg-white dark:bg-black font-semibold text-gray-800 dark:text-gray-200"
              >
                <option value="58">58mm Roll</option>
                <option value="80">80mm Roll</option>
              </select>

              <select
                value={billCopies}
                onChange={(e) => handleSetBillCopies(e.target.value)}
                className="text-sm rounded-lg px-3 py-2 border border-emerald-300 dark:border-emerald-700 bg-white dark:bg-black font-semibold text-gray-800 dark:text-gray-200"
                title="Number of copies printed per customer bill"
              >
                <option value="1">1 Bill Copy</option>
                <option value="2">2 Bill Copies</option>
                <option value="3">3 Bill Copies</option>
                <option value="4">4 Bill Copies</option>
              </select>

              {defaultBillPrinter && (
                <button
                  onClick={() => handleTestPrint({
                    name: defaultBillPrinter,
                    isIp: defaultBillPrinter.startsWith("IP:"),
                    ip: defaultBillPrinter.replace(/^IP:/i, '').split(':')[0],
                    port: defaultBillPrinter.includes(':') ? parseInt(defaultBillPrinter.split(':')[1]) : 9100,
                    format: pageFormat
                  })}
                  disabled={testingPrinter === defaultBillPrinter}
                  className="btn btn-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1 shadow"
                >
                  <IconSend size={16} /> Test Default
                </button>
              )}
            </div>
          </div>

          {/* SECTION: WiFi / Network Printers */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div>
                <h4 className="text-xl font-bold flex items-center gap-2">
                  <IconWifi size={24} className="text-teal-600" /> WiFi & LAN Network Printers (Port 9100)
                </h4>
                <p className="text-xs text-gray-500">
                  Connect wireless and Ethernet thermal printers directly without installing OS drivers.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={scanNetworkPrinters}
                  disabled={scanningNetwork}
                  className="flex items-center gap-2 btn btn-outline btn-sm rounded-lg border-teal-500 text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-950 font-semibold"
                >
                  <IconWifi size={16} className={scanningNetwork ? "animate-pulse text-teal-600" : ""} />
                  {scanningNetwork ? "Scanning Subnet..." : "Discover WiFi Printers"}
                </button>
                <button
                  onClick={() => setShowAddIpModal(true)}
                  className="flex items-center gap-2 btn btn-sm rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold shadow"
                >
                  <IconPlus size={16} /> Add IP Printer
                </button>
              </div>
            </div>

            {/* Network Printers Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {networkPrinters.length === 0 ? (
                <div className="col-span-full text-center py-8 bg-gray-50 dark:bg-black/30 rounded-2xl border border-dashed border-gray-300">
                  <IconWifi size={36} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500 font-medium">No WiFi/Network printers configured yet.</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Click <b>Discover WiFi Printers</b> to auto-scan your subnet, or <b>Add IP Printer</b> to configure one manually.
                  </p>
                </div>
              ) : (
                networkPrinters.map((np) => {
                  const isCurrentDefault = defaultBillPrinter === `IP:${np.ip}:${np.port}`;
                  const isTesting = testingPrinter === `net_${np.ip}`;
                  return (
                    <div
                      key={np.id}
                      className={`p-5 bg-white dark:bg-restro-gray border rounded-2xl flex flex-col justify-between gap-4 shadow-sm transition hover:shadow-md ${
                        isCurrentDefault ? "border-emerald-500 ring-2 ring-emerald-500/20" : "border-restro-border-green"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-xl bg-teal-50 dark:bg-teal-950/50 flex items-center justify-center text-teal-600 flex-shrink-0">
                          <IconWifi size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <h4 className="font-bold truncate text-base" title={np.name}>{np.name}</h4>
                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200">
                              {np.format || "80"}mm
                            </span>
                          </div>
                          <div className="text-xs font-mono text-gray-600 dark:text-gray-300 mt-0.5">
                            {np.ip}:{np.port || 9100}
                          </div>
                          {isCurrentDefault && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 mt-1">
                              <IconStar size={12} className="fill-emerald-500" /> Default Bill Printer
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                        <button
                          onClick={() => handleTestPrint({
                            name: np.name,
                            isIp: true,
                            ip: np.ip,
                            port: np.port,
                            format: np.format
                          })}
                          disabled={isTesting}
                          className="flex-1 btn btn-outline btn-xs rounded-lg text-teal-700 border-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900 font-semibold flex items-center justify-center gap-1"
                        >
                          <IconSend size={14} className={isTesting ? "animate-spin" : ""} />
                          {isTesting ? "Printing..." : "Test Print"}
                        </button>

                        {!isCurrentDefault ? (
                          <button
                            onClick={() => handleSetDefaultBillPrinter(`IP:${np.ip}:${np.port}`)}
                            className="btn btn-outline btn-xs rounded-lg text-emerald-700 border-emerald-300 hover:bg-emerald-50 font-semibold"
                            title="Set as Default Bill Printer"
                          >
                            Set Default
                          </button>
                        ) : null}

                        <button
                          onClick={() => handleDeleteNetworkPrinter(np.id, np.name)}
                          className="btn btn-ghost btn-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg p-1"
                          title="Remove Printer"
                        >
                          <IconTrash size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* SECTION: Discovered OS Printers */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div>
                <h4 className="text-xl font-bold flex items-center gap-2">
                  <IconDeviceDesktop size={24} className="text-restro-green" /> Discovered OS Printers
                </h4>
                <p className="text-xs text-gray-500">
                  Printers installed in your operating system (USB thermal printers, CUPS, or Windows Spooler).
                </p>
              </div>

              <button
                onClick={fetchPrinters}
                disabled={loading}
                className="flex items-center gap-2 btn btn-outline btn-sm rounded-lg border-restro-border-green w-fit font-semibold"
              >
                <IconRefresh size={16} className={loading ? "animate-spin" : ""} /> Refresh OS Printers
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {printers.length === 0 && !loading ? (
                <div className="col-span-full text-center py-8 bg-gray-50 dark:bg-black/30 rounded-2xl border border-dashed border-gray-300">
                  <p className="text-gray-500">
                    {isElectron 
                      ? "No local OS printers discovered. Make sure printers are added in your operating system printer settings."
                      : "No printers discovered. Make sure the local print agent is running."
                    }
                  </p>
                </div>
              ) : (
                printers.map((printer, index) => {
                  const isCurrentDefault = defaultBillPrinter === printer.name;
                  const isTesting = testingPrinter === printer.name;
                  return (
                    <div
                      key={index}
                      className={`p-5 bg-white dark:bg-restro-gray border rounded-2xl flex flex-col justify-between gap-4 shadow-sm transition hover:shadow-md ${
                        isCurrentDefault ? "border-emerald-500 ring-2 ring-emerald-500/20" : "border-restro-border-green"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-xl bg-restro-border-green-light flex items-center justify-center text-restro-green-dark flex-shrink-0">
                          <SafeIconPrinter size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold truncate text-base" title={printer.name}>
                            {printer.displayName || printer.name}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`w-2 h-2 rounded-full ${printer.status === 0 ? 'bg-green-500' : 'bg-green-500'}`}></span>
                            <span className="text-[10px] uppercase font-semibold text-gray-500">
                              {printer.isDefault ? "OS System Default" : "Local / USB Queue"}
                            </span>
                          </div>
                          {isCurrentDefault && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 mt-1">
                              <IconStar size={12} className="fill-emerald-500" /> Default Bill Printer
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                        <button
                          onClick={() => handleTestPrint({
                            name: printer.name,
                            isIp: false,
                            format: pageFormat
                          })}
                          disabled={isTesting}
                          className="flex-1 btn btn-outline btn-xs rounded-lg text-restro-green-dark border-restro-border-green hover:bg-restro-button-hover font-semibold flex items-center justify-center gap-1"
                        >
                          <IconSend size={14} className={isTesting ? "animate-spin" : ""} />
                          {isTesting ? "Printing..." : "Test Print"}
                        </button>

                        {!isCurrentDefault && (
                          <button
                            onClick={() => handleSetDefaultBillPrinter(printer.name)}
                            className="btn btn-outline btn-xs rounded-lg text-emerald-700 border-emerald-300 hover:bg-emerald-50 font-semibold"
                            title="Set as Default Bill Printer"
                          >
                            Set Default
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Web Bluetooth Configuration (Browser Mode Only) */}
          {!isElectron && (
            <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h4 className="font-bold mb-1 flex items-center gap-2 text-blue-800 dark:text-blue-300">
                    <IconBluetooth size={22} /> Direct Bluetooth Printing (Web Bluetooth)
                  </h4>
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    Connect directly to a Bluetooth thermal printer from your browser (Chrome/Edge on Android, Windows, Mac).
                  </p>
                </div>
                
                <div className="flex items-center gap-3">
                  {btConnected ? (
                    <div className="flex flex-col items-end">
                      <span className="text-sm font-bold text-green-600 flex items-center gap-1">
                        <IconCheck size={16} /> Connected to: {btDeviceName}
                      </span>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={async () => {
                            try {
                              toast.loading("Sending test page...");
                              await printViaWebBluetooth(null, "================================\n       BLUETOOTH TEST PRINT     \n================================\nStatus: Success\n================================\n\n\n");
                              toast.dismiss();
                              toast.success("Printed successfully");
                            } catch (e) {
                              toast.dismiss();
                              toast.error(`Test print failed: ${e.message}`);
                            }
                          }}
                          className="btn btn-outline btn-xs rounded-lg text-blue-700 border-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800"
                        >
                          Test Print
                        </button>
                        <button
                          onClick={() => {
                            const device = getBluetoothDevice();
                            if (device && device.gatt.connected) device.gatt.disconnect();
                          }}
                          className="btn btn-outline btn-xs rounded-lg text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900"
                        >
                          Disconnect
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={connectBluetoothPrinter}
                      className="rounded-lg transition active:scale-95 hover:shadow-lg px-4 py-2 text-white border border-blue-600 bg-blue-600 hover:bg-blue-700 font-semibold text-sm flex items-center gap-2"
                    >
                      <IconBluetooth size={18} /> Pair Bluetooth Printer
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────── */}
      {/* TAB 2: CATEGORY KOT ROUTING                                  */}
      {/* ───────────────────────────────────────────────────────────── */}
      {activeTab === "categories" && (
        <div className="space-y-6">
          <div className="p-5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl flex items-start gap-3">
            <IconInfoCircle size={24} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-blue-900 dark:text-blue-300 text-base">
                How Category KOT Routing Works
              </h4>
              <p className="text-sm text-blue-800 dark:text-blue-400 mt-1">
                Assign menu categories to specific kitchen, pantry, or bar printers. When punching orders on POS or KDS, 
                order items will be automatically split and sent directly to the assigned printer. 
                Categories set to <i>"None"</i> will route to your Default Bill Printer.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto border border-restro-border-green rounded-2xl bg-white dark:bg-restro-gray shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-black/30 border-b border-restro-border-green text-gray-500 font-semibold text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 w-16">#</th>
                  <th className="px-4 py-3">Category Title</th>
                  <th className="px-4 py-3">Assigned Kitchen / Bar Printer</th>
                  <th className="px-4 py-3 w-32 text-center">KOT Copies</th>
                  <th className="px-4 py-3 w-36 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
                {categoriesLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-500">
                      Loading categories...
                    </td>
                  </tr>
                ) : categories.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-500">
                      No categories found. Create categories first in Settings &gt; Categories.
                    </td>
                  </tr>
                ) : (
                  categories.map((cat, idx) => {
                    const isSaving = savingCategory === cat.id;
                    return (
                      <tr key={cat.id} className="hover:bg-gray-50/50 dark:hover:bg-black/20 transition">
                        <td className="px-4 py-3.5 font-medium text-gray-400">{idx + 1}</td>
                        <td className="px-4 py-3.5 font-bold text-gray-900 dark:text-gray-100">
                          {cat.title}
                          {!cat.is_enabled && (
                            <span className="ml-2 text-[10px] px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                              Hidden
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <select
                              value={cat.printer || ""}
                              onChange={(e) => handleAssignCategoryPrinter(cat, e.target.value)}
                              disabled={isSaving}
                              className="text-sm rounded-lg px-3 py-1.5 border border-restro-border-green bg-white dark:bg-black font-semibold text-gray-800 dark:text-gray-200 focus:outline-restro-border-green w-full max-w-sm"
                            >
                              <option value="">-- No Printer Assigned (Default) --</option>
                              <optgroup label="OS Installed Printers">
                                {printers.map((p, pIdx) => (
                                  <option key={`p-os-${pIdx}`} value={p.name}>
                                    🖨️ {p.displayName || p.name}
                                  </option>
                                ))}
                              </optgroup>
                              {networkPrinters.length > 0 && (
                                <optgroup label="WiFi / Network Printers">
                                  {networkPrinters.map((np, nIdx) => (
                                    <option key={`p-net-${nIdx}`} value={`IP:${np.ip}:${np.port}`}>
                                      📶 {np.name} ({np.ip}:{np.port})
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                            </select>
                            {cat.printer && (
                              <span className="text-xs text-emerald-600 font-bold flex items-center gap-0.5">
                                <IconCheck size={16} /> Saved
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <select
                            value={categoryCopies[cat.id] || 1}
                            onChange={(e) => handleSetCategoryCopies(cat.id, e.target.value)}
                            className="text-xs rounded-lg px-2.5 py-1.5 border border-restro-border-green bg-white dark:bg-black font-semibold text-gray-800 dark:text-gray-200 focus:outline-none w-28 text-center"
                            title="Number of KOT copies to print when an order contains items from this category"
                          >
                            <option value="1">1 Copy</option>
                            <option value="2">2 Copies</option>
                            <option value="3">3 Copies</option>
                            <option value="4">4 Copies</option>
                            <option value="5">5 Copies</option>
                          </select>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <button
                            onClick={() => handleTestCategoryKOT(cat)}
                            className="btn btn-outline btn-xs rounded-lg text-restro-green-dark border-restro-border-green hover:bg-restro-button-hover font-semibold inline-flex items-center gap-1"
                            title={`Send test KOT to ${cat.printer || 'Default Printer'} (${categoryCopies[cat.id] || 1}x)`}
                          >
                            <IconSend size={12} /> Test KOT
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────── */}
      {/* MODAL: ADD MANUAL IP / NETWORK PRINTER                       */}
      {/* ───────────────────────────────────────────────────────────── */}
      {showAddIpModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-restro-gray border border-restro-border-green rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-lg font-bold flex items-center gap-2 text-teal-800 dark:text-teal-300">
                <IconWifi size={22} /> Add Network / WiFi Printer
              </h3>
              <button
                onClick={() => setShowAddIpModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="my-4 space-y-4 text-sm">
              <div>
                <label className="block text-gray-500 font-medium mb-1">Printer Friendly Name</label>
                <input
                  type="text"
                  placeholder="e.g. Kitchen Thermal, Bar WiFi Printer"
                  value={ipForm.name}
                  onChange={(e) => setIpForm({ ...ipForm, name: e.target.value })}
                  className="w-full rounded-lg px-3 py-2 border border-restro-border-green dark:bg-black focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-gray-500 font-medium mb-1">IP Address *</label>
                <input
                  type="text"
                  placeholder="e.g. 192.168.1.100"
                  value={ipForm.ip}
                  onChange={(e) => setIpForm({ ...ipForm, ip: e.target.value })}
                  className="w-full rounded-lg px-3 py-2 border border-restro-border-green dark:bg-black focus:outline-none font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-500 font-medium mb-1">Port</label>
                  <input
                    type="number"
                    placeholder="9100"
                    value={ipForm.port}
                    onChange={(e) => setIpForm({ ...ipForm, port: e.target.value })}
                    className="w-full rounded-lg px-3 py-2 border border-restro-border-green dark:bg-black focus:outline-none font-mono"
                  />
                  <span className="text-[10px] text-gray-400">Default: 9100</span>
                </div>

                <div>
                  <label className="block text-gray-500 font-medium mb-1">Paper Roll Format</label>
                  <select
                    value={ipForm.format}
                    onChange={(e) => setIpForm({ ...ipForm, format: e.target.value })}
                    className="w-full rounded-lg px-3 py-2 border border-restro-border-green dark:bg-black focus:outline-none font-semibold"
                  >
                    <option value="80">80mm Roll</option>
                    <option value="58">58mm Roll</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
              <button
                onClick={() => setShowAddIpModal(false)}
                className="btn btn-ghost btn-sm rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyAndSaveIpPrinter}
                disabled={verifyingIp}
                className="btn btn-sm rounded-lg bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-1 font-semibold"
              >
                <IconCheck size={16} />
                {verifyingIp ? "Verifying..." : "Save Printer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
